import { Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';

import { Image } from 'Components/Image';
import { TwoUp } from 'Components/TwoUp';

import proseScientistsInLabCoatsImageDesktopSrc from 'assets/images/homeRevamp/prose-scientists-in-lab-coats-desktop.jpg';
import proseScientistsInLabCoatsImageMobileSrc from 'assets/images/homeRevamp/prose-scientists-in-lab-coats-mobile.jpg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const StyledImage = styled(Image)`
  height: auto;
  width: 100%;
`;

const TextBackground = styled.div`
  background-color: ${theme.colors.primary[300]};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing['10x']} ${theme.spacing['6x']};
  gap: ${theme.spacing['4x']};
  width: 100%;

  ${mediaMinWidth('md')} {
    gap: ${theme.spacing['3x']};
    padding-left: ${theme.spacing['15x']};

    & > * {
      max-width: 417px;
    }
  }

  ${mediaMinWidth('lg')} {
    padding-left: 0;

    & > * {
      /* To allow full spread of the text when in lg format */
      min-width: 520px;
    }
  }
`;

type ScienceTeamProps = {
  title: string;
  description: string;
};

export const ScienceTeam = ({ title, description }: ScienceTeamProps) => {
  const { isMobile, isDesktop } = useResponsiveVariant({ legacy: false });
  return (
    <TwoUp borders={{ radius: true }}>
      <StyledImage
        alt="Prose R&D team in lab coats at their innovative facility"
        src={
          isMobile
            ? proseScientistsInLabCoatsImageMobileSrc
            : proseScientistsInLabCoatsImageDesktopSrc
        }
      />

      <TextBackground data-testid="home-revamp-science-team">
        <Text
          as="h2"
          color="neutral.100"
          style={{ width: '100%' }} // Needed to keep this text aligned with the bottom text bc at a certain point they are different widths
          variant={isDesktop ? 'h3' : 'h4'}
        >
          {title}
        </Text>
        <Text as="p" color="neutral.100" variant={isDesktop ? 'body3regular' : 'body4regular'}>
          {description}
        </Text>
      </TextBackground>
    </TwoUp>
  );
};
